/**
 * Utilities for the Wealthica2GoldContainer component
 * 
 * This file contains utility functions used by the Wealthica2GoldContainer
 * to manage positions, accounts, and other calculations.
 */

import { WealthicaAccount } from '../../types';
import { WealthicaStorage } from '../../utils/storage';
import { fetchPositionsForAccounts } from '../../utils/positions-utils';
import { organizePositionsByAccount, AccountPositionsMap, AccountData } from "../../utils/account-position-utils";
import { getCachedPositionsArray, setCachedPositions, getPositionsCacheKey } from '../../utils/cache-utils';
// Store imports removed: persistence is toggle-driven elsewhere; this util should not mutate store
import { toast } from 'sonner';
import { logger } from '@/services/LoggingService';

/**
 * Interface for accounts with associated positions
 */
export interface AccountWithPositions {
  id: string;
  positions: any[];
}

// Interface for state setters to update UI
export interface LoadingStateSetters {
  setAccountsWithPositions: (value: AccountWithPositions[]) => void;
  setOrganizedPositionsMap: (value: Record<string, any>) => void;
  setIsLoadingPositions: (value: boolean) => void;
  setLoadingPositionsFor: (value: Record<string, boolean>) => void;
}

/**
 * Helper function to calculate account balance from positions
 * 
 * @param accountId - ID of the account to calculate balance for
 * @param wealthicaAccounts - Array of all Wealthica accounts
 * @param accountsWithPositions - Array of accounts with their positions
 * @returns The calculated account balance
 */
export function calculateAccountBalanceFromPositions(
  accountId: string, 
  wealthicaAccounts: WealthicaAccount[],
  accountsWithPositions: AccountWithPositions[]
): number {
  // Get the original account from the API data
  const originalAccount = wealthicaAccounts.find(a => a.id === accountId);
  const apiBalance = originalAccount?.balance || 0;
  
  // Find account with positions data
  const accountWithPositions = accountsWithPositions.find(a => a.id === accountId);
  
  // HYBRID APPROACH:
  // 1. For accounts WITHOUT positions (cash accounts): Use API balance
  // 2. For accounts WITH positions (investment accounts): Sum position market values
  if (!accountWithPositions?.positions?.length) {
    // Cash-only accounts: use API balance
    return apiBalance;
  }
  
  // For investment accounts: Calculate sum of position market values
  const positionSum = accountWithPositions.positions.reduce((total, position) => {
    const marketValue = position.market_value || 0;
    return total + marketValue;
  }, 0);

  // Log difference for debugging and reconciliation
  const difference = apiBalance - positionSum;
  const accountName = originalAccount?.name || 'Unknown';
  const currency = originalAccount?.currency || 'CAD';
  
  // Detailed logging for account balance reconciliation
  if (Math.abs(difference) > 1) { // Only log significant differences (> $1)
    logger.info('BALANCE_RECONCILIATION', `Account: ${accountName} (${accountId})`, {
      apiBalance,
      positionSum,
      difference,
      percentageDiff: apiBalance > 0 ? ((difference / apiBalance) * 100).toFixed(2) + '%' : 'N/A',
      currency,
      positionCount: accountWithPositions.positions?.length || 0,
      balanceSource: 'position_market_values',
      timestamp: new Date().toISOString()
    });
  }
  
  // Return position-based sum for accounts with positions
  return positionSum;
}

/**
 * Batch load all positions
 * 
 * @param wealthicaAccounts - Array of Wealthica accounts
 * @param connectedInstitutionId - Current connected institution ID
 * @param setLoadingStates - Optional state setters to update UI
 * @param persist - Whether to save positions in cache for future visits
 * @returns Object containing accounts with positions and organized positions map
 */
export async function loadAllPositions(
  wealthicaAccounts: WealthicaAccount[],
  connectedInstitutionId?: string,
  setLoadingStates?: LoadingStateSetters,
  persist: boolean = true
): Promise<{
  accountsWithPositions: AccountWithPositions[];
  organizedPositionsMap: Record<string, any>;
  rawPositions: any[];
}> {
  try {
    if (setLoadingStates) {
      setLoadingStates.setIsLoadingPositions(true);
    }
    
    // Initialize loading state for each account
    const newLoadingState: Record<string, boolean> = {};
    wealthicaAccounts.forEach(account => {
      newLoadingState[account.id] = true;
    });
    
    if (setLoadingStates) {
      setLoadingStates.setLoadingPositionsFor(newLoadingState);
    }
    
    // Get the institution ID for position lookup
    const institutionId = connectedInstitutionId || WealthicaStorage.getConnectionData()?.institutionId || '';
    if (!institutionId) {
      logger.debug('wealthica2.loadAllPositions', 'No institution ID available - returning empty positions (normal when disconnected)', {
        provider: 'wealthica2',
        connectedInstitutionId,
        storageInstitutionId: WealthicaStorage.getConnectionData()?.institutionId || '',
        timestamp: new Date().toISOString()
      });
      // Don't show error toast - this is normal when user has no connected accounts
      return { 
        accountsWithPositions: [], 
        organizedPositionsMap: {},
        rawPositions: []
      };
    }
    
    logger.info('wealthica2.loadAllPositions', 'Starting position loading process', {
      provider: 'wealthica2',
      institutionId,
      accountCount: wealthicaAccounts.length,
      accountIds: wealthicaAccounts.map(acc => acc.id),
      timestamp: new Date().toISOString()
    });
    
    // First try to get cached positions
    const cachedPositions = getCachedPositionsArray(institutionId);
    let allPositions: any[] = [];
    
    // Use cached positions if available, otherwise fetch from API
    if (cachedPositions && cachedPositions.length > 0) {
      logger.info('wealthica2.loadAllPositions', 'Using cached positions', {
        provider: 'wealthica2',
        institutionId,
        cachedPositionCount: cachedPositions.length,
        timestamp: new Date().toISOString()
      });
      allPositions = cachedPositions;
    } else {
      logger.info('wealthica2.loadAllPositions', 'No cached positions found, fetching from API', {
        provider: 'wealthica2',
        institutionId,
        timestamp: new Date().toISOString()
      });
      try {
        // Extract account IDs for the API call
        const accountIds = wealthicaAccounts.map(account => account.id);
        
        if (accountIds.length === 0) {
          console.log('No accounts to fetch positions for');
          return { 
            accountsWithPositions: [], 
            organizedPositionsMap: {},
            rawPositions: []
          };
        }
        
        // Fetch positions for all accounts in one batch call
        const fetchedPositionsResult = await fetchPositionsForAccounts(accountIds);
        
        // Update with fetched positions
        if (fetchedPositionsResult.success && 
            fetchedPositionsResult.positions && 
            Array.isArray(fetchedPositionsResult.positions) && 
            (fetchedPositionsResult.positions?.length || 0) > 0) {
          allPositions = fetchedPositionsResult.positions;
          logger.info('wealthica2.loadAllPositions', `Fetched positions from API`, {
            count: allPositions.length
          });
          
          // Always cache positions to ensure they're available for persistence toggle
          setCachedPositions(institutionId, allPositions);
          
          // Verify positions were cached correctly
          {
            const cacheKey = getPositionsCacheKey(institutionId);
            logger.debug('wealthica2.loadAllPositions', 'Position caching verification', {
              originalPositionsLength: allPositions.length,
              institutionId,
              cacheKey,
              verifyCacheExists: WealthicaStorage.getItem(cacheKey) !== null,
              timestamp: new Date().toISOString()
            });
          }
          
          // Double-check retrieval
          const verificationPositions = getCachedPositionsArray(institutionId);
          logger.debug('wealthica2.loadAllPositions', 'Retrieved positions verification', {
            verificationPositionsLength: verificationPositions.length,
            positionIdsMatch: allPositions.length === verificationPositions.length,
            timestamp: new Date().toISOString()
          });
        } else {
          logger.warn('wealthica2.loadAllPositions', 'No positions returned from API');
        }
      } catch (fetchError) {
        logger.error('wealthica2.loadAllPositions', 'Failed to fetch positions', { error: fetchError });
        toast.error('Failed to load your investment positions.');
      }
    }
    
    // Process positions and group by account using our updated utility
    // The organizePositionsByAccount function now handles all position formats
    logger.info('wealthica2.loadAllPositions', 'Organizing positions according to Wealthica API spec', {
      provider: 'wealthica2',
      positionCount: allPositions?.length || 0,
      timestamp: new Date().toISOString()
    });
    
    // Create an account info map for display names
    // Simply map account IDs to display names as expected by organizePositionsByAccount
    const accountsInfoMap: Record<string, string> = {};
    wealthicaAccounts.forEach(account => {
      accountsInfoMap[account.id] = account.name || account.id;
    });
    
    // Organize positions by account and class following the Wealthica API spec
    logger.info('wealthica2.loadAllPositions', 'About to organize positions', {
      positionsToOrganize: allPositions.length,
      accountsInfoMapKeys: Object.keys(accountsInfoMap),
      samplePositionStructure: allPositions[0] ? {
        _id: allPositions[0]._id,
        hasInvestments: !!allPositions[0].investments,
        investmentsCount: allPositions[0].investments?.length || 0,
        sampleInvestment: allPositions[0].investments?.[0] ? {
          accountId: allPositions[0].investments[0].investment,
          institution: allPositions[0].investments[0].institution
        } : null
      } : null
    });
    
    const { accountMap, positionsMap } = organizePositionsByAccount(allPositions, accountsInfoMap);
    
    logger.info('wealthica2.loadAllPositions', 'Position organization completed', {
      accountMapKeys: Object.keys(accountMap),
      accountMapSize: Object.keys(accountMap).length,
      totalPositionsInAccountMap: Object.values(accountMap).reduce((sum, positions) => sum + positions.length, 0)
    });
    
    // Create array of accounts with positions for UI display
    const newAccountsWithPositions: AccountWithPositions[] = [];
    
    // Process accountMap into the format needed by the UI
    if (accountMap && typeof accountMap === 'object') {
      Object.keys(accountMap).forEach(accountId => {
        const accountPositions = accountMap[accountId] || [];
        newAccountsWithPositions.push({
          id: accountId,
          positions: accountPositions
        });
        
        logger.debug('wealthica2.loadAllPositions', 'Added account to UI list', {
          accountId,
          positionCount: accountPositions.length,
          firstPosition: accountPositions[0] ? {
            id: accountPositions[0]._id,
            securityName: accountPositions[0].security?.name,
            marketValue: accountPositions[0].market_value
          } : null
        });
      });
      
      logger.info('wealthica2.loadAllPositions', 'Successfully organized positions', {
        provider: 'wealthica2',
        accountCount: Object.keys(accountMap).length,
        totalAccountsWithPositions: newAccountsWithPositions.length,
        totalPositionsAcrossAccounts: newAccountsWithPositions.reduce((sum, acc) => sum + acc.positions.length, 0),
        timestamp: new Date().toISOString()
      });
    } else {
      logger.warn('wealthica2.loadAllPositions', 'No positions found for any accounts', {
        provider: 'wealthica2',
        accountMapType: typeof accountMap,
        accountMapKeys: accountMap ? Object.keys(accountMap) : 'null',
        timestamp: new Date().toISOString()
      });
    }
    
    // Create a properly typed positions map
    const typedPositionsMap: Record<string, any> = {};
    
    // Process positionsMap for UI consumption
    if (positionsMap && typeof positionsMap === 'object') {
      Object.entries(positionsMap).forEach(([key, value]) => {
        typedPositionsMap[key] = value;
      });
    }
    
    // Do not mutate the store or trigger persistence here. Persistence is driven by user toggle elsewhere.
    
    // If state setters are provided, update UI state
    if (setLoadingStates) {
      setLoadingStates.setAccountsWithPositions(newAccountsWithPositions);
      setLoadingStates.setOrganizedPositionsMap(typedPositionsMap);
      setLoadingStates.setIsLoadingPositions(false);
      
      // Clear loading states for all accounts
      const clearedLoadingState: Record<string, boolean> = {};
      wealthicaAccounts.forEach(account => {
        clearedLoadingState[account.id] = false;
      });
      setLoadingStates.setLoadingPositionsFor(clearedLoadingState);
    }
    
    return {
      accountsWithPositions: newAccountsWithPositions,
      organizedPositionsMap: typedPositionsMap,
      rawPositions: allPositions
    };
    
  } catch (error) {
    logger.error('wealthica2.loadAllPositions', 'General error loading positions', { error });
    toast.error('Failed to load your investment positions.');
    
    if (setLoadingStates) {
      setLoadingStates.setIsLoadingPositions(false);
    }
    
    return {
      accountsWithPositions: [],
      organizedPositionsMap: {},
      rawPositions: []
    };
  }
}
